import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Clock, Tag, Trash2, Loader2, Pencil } from "lucide-react";
import { CATEGORIES } from "../lib/constants";
import { ReportButton } from "./ReportButton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export function ServiceCard({ service, index = 0, canDelete = false, onDelete, canEdit = false, onEdit, currentUserId }) {
  const cats = (service.categories && service.categories.length ? service.categories : [service.category]).filter(Boolean);
  const catLabels = cats.map((v) => CATEGORIES.find((c) => c.value === v)?.label || v);
  const isOwner = canEdit || canDelete || (currentUserId && service.runner_id === currentUserId);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirmDelete = async () => {
    setBusy(true);
    await onDelete?.(service.id);
    setBusy(false);
    setOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.4) }}
      data-testid={`service-card-${service.id}`}
      className="relative h-full bg-white rounded-3xl p-6 border border-gray-100 shadow-soft hover:shadow-lift transition-all duration-300 hover:-translate-y-1"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex flex-wrap gap-1.5">
          {catLabels.map((label) => (
            <span key={label} className="text-xs font-bold uppercase tracking-wide text-secondary bg-secondary/10 px-3 py-1 rounded-full">
              {label}
            </span>
          ))}
        </div>
        {service.rate && (
          <span className="font-heading font-extrabold text-primary text-lg whitespace-nowrap">{service.rate}</span>
        )}
      </div>

      <h3 className="font-heading font-extrabold text-lg text-[#1A1D20] leading-snug mb-2">{service.title}</h3>
      <p className="text-sm text-[#6B7280] line-clamp-3 mb-4">{service.description}</p>

      <div className="flex flex-wrap items-center gap-4 text-xs text-[#6B7280] mb-4">
        <span className="flex items-center gap-1"><MapPin size={13} /> {service.location}</span>
        {service.availability && <span className="flex items-center gap-1"><Clock size={13} /> {service.availability}</span>}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <Link to={`/profile/${service.runner_id}`} data-testid={`service-runner-${service.id}`}
          className="flex items-center gap-2 group">
          <span className="w-7 h-7 rounded-full bg-secondary/20 text-secondary flex items-center justify-center text-xs font-bold">
            {service.runner_name?.[0]?.toUpperCase()}
          </span>
          <span className="text-xs font-semibold text-[#4A5361] group-hover:text-primary transition-colors">{service.runner_name}</span>
        </Link>
        {(canEdit || canDelete) ? (
          <div className="flex items-center gap-2">
            {canEdit && (
              <button type="button" data-testid={`edit-service-${service.id}`} aria-label="Edit service"
                onClick={() => onEdit?.(service)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#2C6FA0] bg-[#E6F1FA]/70 hover:bg-[#E6F1FA] transition-colors">
                <Pencil size={15} />
              </button>
            )}
            {canDelete && (
              <button type="button" data-testid={`delete-service-${service.id}`} aria-label="Remove service"
                onClick={() => setOpen(true)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#B23B3B] bg-[#FBE9E9]/60 hover:bg-[#FBE9E9] transition-colors">
                <Trash2 size={15} />
              </button>
            )}
          </div>
        ) : (
          !isOwner && <ReportButton targetType="service" targetId={service.id} />
        )}
      </div>

      {canDelete && (
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogContent className="rounded-3xl" data-testid={`delete-service-dialog-${service.id}`}>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Remove this service?</AlertDialogTitle>
              <AlertDialogDescription>
                "{service.title}" will no longer appear in the Services directory.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`delete-service-no-${service.id}`} className="rounded-full font-semibold">Keep it</AlertDialogCancel>
              <AlertDialogAction data-testid={`delete-service-yes-${service.id}`} onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={busy}
                className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">
                {busy ? <Loader2 className="animate-spin" size={16} /> : "Remove"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </motion.div>
  );
}
